import { ButtonV2, Checkbox, Icon, Preloader, SelectGroup, TooltipTrigger } from 'components';
import {
  ADMIN_DEFAULTS,
  CORP_PERMISSIONS,
  getPermissionMap,
  NON_ADMIN_DEFAULTS,
  PERMISSIONS,
  SCHOOL_PERMISSIONS
} from 'constants/permissions';
import useLaunchDarkly from 'hooks/useLaunchDarkly';
import get from 'lodash/get';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import './style.scss';

const pickSingleLevel = val => {
  if (!Array.isArray(val)) return val;
  if (val.includes('write')) return 'write';
  if (val.includes('read')) return 'read';
  return 'none';
};

const EditRole = ({ onClose, currentSchool, corpSchool, currentUser, tenant, data }) => {
  const flags = useLaunchDarkly();
  const initialRole = data.permissionRole || {};
  const roles = data.permissionRoles || [];
  const isViewOnly = data.view;
  const editDisabled = data.editDisabled;

  const [role, setRole] = useState(initialRole);
  const [copyRole, setCopyRole] = useState('none');

  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [isAdmin, setIsAdmin] = useState(!!role.is_admin);
  const [isLoading, setIsLoading] = useState(!!initialRole.id);

  const permissionMapWithOverrides = getPermissionMap(flags);
  const ALL_PERMISSIONS = (corpSchool ? CORP_PERMISSIONS : PERMISSIONS)
    .filter(key => {
      const hideFlag = permissionMapWithOverrides[key].showPermission;
      return !hideFlag || flags[hideFlag];
    })
    .sort((a, b) => permissionMapWithOverrides[a].label.localeCompare(permissionMapWithOverrides[b].label));

  useEffect(() => {
    if (initialRole.id) {
      fetchRoleById(initialRole.id);
    }
  }, [initialRole.id, fetchRoleById]);

  useEffect(() => {
    if (initialRole.id) {
      setName(role.name || '');
    } else if (copyRole === 'none') {
      return;
    }
    setIsAdmin(!!role.is_admin);
    const initPerms = {};
    ALL_PERMISSIONS.forEach(key => {
      const arr = role.line_items?.[key] || [];
      initPerms[key] = pickSingleLevel(arr);
    });
    setPermissions(initPerms);
  }, [role]);

  useEffect(() => {
    let nextPermissions = { ...permissions };
    Object.entries(permissionMapWithOverrides).forEach(([key, obj]) => {
      const disabledOptions = get(obj, `${corpSchool ? 'disabledCorp' : 'disabled'}.${isAdmin ? 'admin' : 'nonAdmin'}`);
      if (disabledOptions?.length === 3) {
        nextPermissions[key] = 'none';

        Object.entries(permissionMapWithOverrides)
          .filter(([depKey, depValue]) => depValue.dependsOn?.includes(key))
          .forEach(([depKey, depValue]) => (nextPermissions[depKey] = 'none'));
      }
    });
    setPermissions(nextPermissions);
  }, [copyRole, isAdmin]);

  const handleChangePermission = (key, level) => {
    setPermissions(prev => {
      const nextPermissions = { ...prev, [key]: level };
      Object.entries(permissionMapWithOverrides)
        .filter(([depKey, depValue]) => depValue.dependsOn?.includes(key))
        .forEach(([depKey, depValue]) => {
          if (level === 'none') {
            nextPermissions[depKey] = 'none';
          } else if (level === 'read' && nextPermissions[depKey] === 'write') {
            nextPermissions[depKey] = 'read';
          }
        });
      return nextPermissions;
    });
  };

  const clearPermissions = () => {
    setPermissions({});
    setIsAdmin(false);
  };

  const fetchRoleById = useCallback(
    permissionId => {
      clearPermissions();

      const schoolId = currentUser?.current_school?.id;
      if (!schoolId || !tenant?.sites?.length) return;

      const matchingSite = tenant.sites.find(site => site.pom_identifier === `PCO:SITE:${schoolId}`);
      if (!matchingSite) return;

      setIsLoading(true);
      req
        .fetchRoleById({ site_id: matchingSite.id, permission_role_id: permissionId })
        .then(result => {
          setRole(result);
        })
        .catch(err => console.error('Error fetching role:', err))
        .finally(() => setIsLoading(false));
    },
    [currentUser, tenant]
  );

  const handleChangeRole = useCallback(
    newRole => {
      setCopyRole(newRole);
      clearPermissions();

      if (newRole === 'none') {
        // leave permissions empty, isAdmin = false
      } else if (newRole === 'nonAdmin') {
        setPermissions({ ...NON_ADMIN_DEFAULTS });
        setIsAdmin(false);
      } else if (newRole === 'admin') {
        setPermissions({ ...ADMIN_DEFAULTS });
        setIsAdmin(true);
      } else {
        const matchedRole = roles.find(r => r.name === newRole);
        if (matchedRole) {
          fetchRoleById(matchedRole.id);
        }
      }
    },
    [roles, fetchRoleById]
  );

  const editRole = useCallback(() => {
    const trimmedName = name.trim();
    if (trimmedName.length < 3) {
      Actions.reportError('Role name must contain at least 3 characters');
      return;
    }

    const origName = (initialRole.name || '').trim().toLowerCase();
    const rolesToCheck = initialRole.id ? roles.filter(r => r.name?.trim().toLowerCase() !== origName) : roles;
    if (rolesToCheck.some(r => r.name?.trim().toLowerCase() === trimmedName.toLowerCase())) {
      Actions.reportError('Role name already exists');
      return;
    }

    const schoolId = currentUser?.current_school?.id;
    if (!schoolId || !tenant?.sites?.length) return;
    const matchingSite = tenant.sites.find(site => site.pom_identifier === `PCO:SITE:${schoolId}`);
    if (!matchingSite) return;

    const line_items = {};
    Object.entries(permissions).forEach(([key, lvl]) => {
      line_items[key] = [lvl];
    });

    const request = initialRole.id ? req.editRole : req.createRole;
    const payload = {
      name: trimmedName,
      is_admin: isAdmin,
      is_default_for_site: false,
      line_items
    };

    setSaving(true);
    request({
      site_id: matchingSite.id,
      permission_role_id: initialRole.id,
      ...payload
    })
      .then(result => {
        onClose();
        Actions.showFlash(`${result.name} role has been ${initialRole.id ? 'updated' : 'added'}.`);
      })
      .catch(err => {
        Actions.reportError(`Unable to ${initialRole.id ? 'edit' : 'create'} role`, err);
        setSaving(false);
      });
  }, [initialRole, name, isAdmin, permissions, roles, currentUser, tenant, onClose]);

  const toggleAdmin = () => setIsAdmin(prev => !prev);

  const filteredPermissions = ALL_PERMISSIONS.filter(
    key =>
      (!permissionMapWithOverrides[key].enabledBySchoolSetting ||
        permissionMapWithOverrides[key].enabledBySchoolSetting(currentSchool)) &&
      !SCHOOL_PERMISSIONS.includes(key)
  );
  const schoolPermissions = ALL_PERMISSIONS.filter(
    key =>
      (!permissionMapWithOverrides[key].enabledBySchoolSetting ||
        permissionMapWithOverrides[key].enabledBySchoolSetting(currentSchool)) &&
      SCHOOL_PERMISSIONS.includes(key)
  );

  const renderRow = key => {
    const value = permissions[key];
    const { label, tooltip, dependsOn } = permissionMapWithOverrides[key];
    let disabledKeys = get(
      permissionMapWithOverrides,
      `${key}.${corpSchool ? 'disabledCorp' : 'disabled'}.${isAdmin ? 'admin' : 'nonAdmin'}`,
      []
    );
    if (dependsOn?.length) {
      disabledKeys = [...disabledKeys];
      dependsOn.forEach(depKey => {
        const depValue = permissions[depKey];
        if (depValue === 'none') {
          disabledKeys.push('none', 'read', 'write');
        } else if (depValue === 'read') {
          disabledKeys.push('write');
        }
      });
    }

    return (
      <div key={key} className="edit-role__row" data-testid={`permission-row-${key}`}>
        <div className="edit-role__row-label">
          {label}
          {tooltip && (
            <TooltipTrigger tooltip={tooltip} side="center" className="edit-role__row-tooltip">
              <ButtonV2 icon iconName="tooltip" iconSize={16} />
            </TooltipTrigger>
          )}
        </div>
        <div className="edit-role__row-values">
          <Checkbox
            checked={value === 'none'}
            disabled={editDisabled || disabledKeys.includes('none')}
            onChange={() => handleChangePermission(key, 'none')}
            label="No Access"
          />
          <Checkbox
            checked={value === 'read'}
            disabled={editDisabled || disabledKeys.includes('read')}
            onChange={() => handleChangePermission(key, 'read')}
            label="Read"
          />
          <Checkbox
            checked={value === 'write'}
            disabled={editDisabled || disabledKeys.includes('write')}
            onChange={() => handleChangePermission(key, 'write')}
            label="Full Access"
          />
        </div>
      </div>
    );
  };

  const origName = role.name || '';
  const origAdmin = !!role.is_admin;
  const hasNameChanged = name !== origName;
  const hasAdminChanged = isAdmin !== origAdmin;
  const hasPermsChanged = ALL_PERMISSIONS.some(key => {
    const origArr = role.line_items?.[key] || [];
    return pickSingleLevel(origArr) !== permissions[key];
  });
  const hasChanges = hasNameChanged || hasAdminChanged || hasPermsChanged;
  const isValidName = name.trim().length >= 3;
  const totalRows = filteredPermissions.length + (corpSchool ? schoolPermissions.length : 0);
  const allRowsSelected = Object.keys(permissions).length === totalRows;
  const isSubmitDisabled =
    saving || !isValidName || editDisabled || (initialRole.id ? !hasChanges : copyRole === 'none' && !allRowsSelected);

  return (
    <div className="modal__wrapper edit-role">
      <div className="modal__header modal__header--bordered">
        <div className="modal__header-title">
          {isViewOnly ? 'View Role' : `${initialRole.id ? 'Edit' : 'Add'} Role & Permissions`}
        </div>
      </div>
      <div className="modal__container">
        <div className="edit-role__content">
          <div className="edit-role__role">
            <div className="edit-role__role-name-field">
              <label>Role Title</label>
              <input type="text" disabled={isViewOnly} value={name} onChange={e => setName(e.target.value)} />
            </div>

            {!initialRole.id && (
              <div className="edit-role__role-copy-field">
                <div className="label-con">
                  <label>Copy Permissions From</label>
                  <TooltipTrigger
                    tooltip="Select an existing role to load its permissions, then modify to create a new role."
                    side="right"
                    className="copy-permissions-tooltip"
                  >
                    <Icon name="tooltip" size={16} />
                  </TooltipTrigger>
                </div>
                <SelectGroup
                  type="radio"
                  className="edit-role__role-select"
                  name="role"
                  onChange={handleChangeRole}
                  checked={copyRole}
                  data-cy="change-role"
                >
                  <SelectGroup.Item value="none" label="None" />
                  {roles.map(r => (
                    <SelectGroup.Item key={r.id} value={r.name} label={r.name} />
                  ))}
                </SelectGroup>
              </div>
            )}
          </div>

          {!!initialRole.is_default_for_site && (
            <div className="role-toggle">
              <div className="role-name">{name}</div>
              <TooltipTrigger
                tooltip="System Non-Admin is a locked, default role automatically assigned to staff removed from a custom role. While permissions can be edited, this role cannot be deleted."
                side="right"
                className="copy-permissions-tooltip"
              >
                <Icon name="tooltip" size={16} />
              </TooltipTrigger>
            </div>
          )}

          <div className="role-toggle">
            <Checkbox
              className="role-toggle-checkbox"
              type="slider"
              label="Designate as Admin"
              onChange={toggleAdmin}
              checked={isAdmin}
              disabled={isViewOnly}
            />
            <Icon name="admin" color="#000" className="admin-role" />
            <TooltipTrigger
              tooltip="Sets the custom role as an admin when on or as non-admin when off."
              side="right"
              className="copy-permissions-tooltip"
            >
              <Icon name="tooltip" size={16} />
            </TooltipTrigger>
          </div>

          <div className="edit-role__section">
            {corpSchool && <div className="edit-role__section-title">Corporate Portal</div>}
            {filteredPermissions.map(renderRow)}
          </div>

          {corpSchool && (
            <div className="edit-role__section">
              <div className="edit-role__section-title">School Permission</div>
              {schoolPermissions.map(renderRow)}
            </div>
          )}
        </div>

        <div className="form__row form__row--actions">
          <ButtonV2
            label={initialRole.id ? 'Save changes' : 'Add role'}
            onClick={editRole}
            data-cy="save"
            isLoading={saving}
            disabled={isSubmitDisabled}
          />
        </div>
        {isLoading && (
          <div className="edit-role__loading">
            <Preloader large center />
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  currentSchool: state.currentUser.data.current_school,
  corpSchool: state.currentUser.data.current_school.corp_school,
  currentUser: state.currentUser.data,
  tenant: state.tenant.data
});

export default connect(mapStateToProps)(EditRole);
